import { useEffect, useState } from 'react';

import { format, parseISO } from 'date-fns';
import { useNavigate, useSearchParams } from 'react-router-dom';

import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import PasswordRoundedIcon from '@mui/icons-material/PasswordRounded';
import { useIntl } from '@zenbusiness/application-commons-intl';
import { WebView } from '@zenbusiness/application-commons-webview';
import Button from '@zenbusiness/zui/Button';
import Chip from '@zenbusiness/zui/Chip';
import EmptyState from '@zenbusiness/zui/EmptyState';
import ErrorState from '@zenbusiness/zui/ErrorState';
import Heading from '@zenbusiness/zui/Heading';
import IconButton from '@zenbusiness/zui/IconButton';
import Skeleton from '@zenbusiness/zui/Skeleton';
import Snackbar from '@zenbusiness/zui/Snackbar';
import { useTheme } from '@zenbusiness/zui/styles';
import Text from '@zenbusiness/zui/Text';

import { accountSettingsSecurity } from '#client/constants/routes';
import { useMfaMethods } from '#client/hooks/useMfaMethods';
import useMyAccountOperation from '#client/hooks/useMyAccountOperation';
import { appKeepsSignInState } from '#client/utils/appSession';
import { clearMfaNudgeDecision } from '#client/utils/mfaNudgeStorage';
import { MY_ACCOUNT_OPERATIONS } from '#client/utils/myAccount';
import { generateMfaEnrollmentUrl, generateMfaStepUpUrl } from '#client/utils/urls';

import {
  METHOD_TYPES,
  MFA_STEP_UP_METHOD_PARAM,
  MFA_STEP_UP_PARAM,
  MFA_STEP_UP_REMOVE,
  MFA_STEP_UP_TURN_OFF,
  NOT_FOR_REQUIRE_MFA,
  RENDERED_METHOD_TYPES,
} from './constants';
import { getRemovalBlockedReason } from './removalRules';
import * as Styled from './TwoStepVerification.style';

import { RemoveMfaMethodDialog } from '../RemoveMfaMethodDialog';
import { TurnOffMfaDialog } from '../TurnOffMfaDialog';

export const LOCALIZED_TEXT = {
  title: 'dashboard.accountSettings.twoStepVerification.title',
  on: 'dashboard.accountSettings.twoStepVerification.on',
  emptyStateTitle: 'dashboard.accountSettings.twoStepVerification.emptyState.title',
  emptyStateDescription: 'dashboard.accountSettings.twoStepVerification.emptyState.description',
  emptyStateSmsOnlyDescription: 'dashboard.accountSettings.twoStepVerification.emptyState.smsOnlyDescription',
  emptyStateSetUp: 'dashboard.accountSettings.twoStepVerification.emptyState.setUp',
  add: 'dashboard.accountSettings.twoStepVerification.add',
  addMethod: 'dashboard.accountSettings.twoStepVerification.addMethod',
  added: 'dashboard.accountSettings.twoStepVerification.added',
  lastUsed: 'dashboard.accountSettings.twoStepVerification.lastUsed',
  remove: 'dashboard.accountSettings.twoStepVerification.remove',
  methodAdded: 'dashboard.accountSettings.twoStepVerification.methodAdded',
  turnOff: 'dashboard.accountSettings.twoStepVerification.turnOff',
  turnedOff: 'dashboard.accountSettings.twoStepVerification.turnedOff',
  recoveryCodeLabel: 'dashboard.accountSettings.twoStepVerification.recoveryCode.label',
  recoveryCodeDescription: 'dashboard.accountSettings.twoStepVerification.recoveryCode.description',
  recoveryCodeGenerate: 'dashboard.accountSettings.twoStepVerification.recoveryCode.generate',
  recoveryCodeRegenerate: 'dashboard.accountSettings.twoStepVerification.recoveryCode.regenerate',
  startFailed: 'dashboard.accountSettings.twoStepVerification.startFailed',
  openInBrowser: 'dashboard.accountSettings.twoStepVerification.openInBrowser',
};

const formatMfaDate = (isoDate) => (isoDate ? format(parseISO(isoDate), 'MMM d, yyyy') : null);

const maskPhoneNumber = (phoneNumber) => (phoneNumber ? `••• ••• ${phoneNumber.slice(-4)}` : null);

const methodDetail = (key, method) => (key === 'sms' ? maskPhoneNumber(method?.phoneNumber) : method?.name);

// "Set it up" names no factor, so Auth0 starts with the required text message. Each "Add" names
// its own factor.
const enrollmentUrl = (mfaFactor) => generateMfaEnrollmentUrl(accountSettingsSecurity, mfaFactor);

// Enrollment happens in Auth0 and comes back with a full page load, so the ids enrolled before
// leaving are kept here to tell which method is new and confirm it.
const ENROLLED_IDS_KEY = 'twoStepVerification.enrolledIds';

const readEnrolledIdsBeforeAdd = () => {
  try {
    return JSON.parse(sessionStorage.getItem(ENROLLED_IDS_KEY));
  } catch {
    return null;
  }
};

function TwoStepVerification() {
  const { formatMessage: fm } = useIntl();
  const { methods, requireMfa, loading, error, refetch } = useMfaMethods();
  const navigate = useNavigate();
  const theme = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const [methodToRemove, setMethodToRemove] = useState(null);
  const [enrolledIdsBeforeAdd] = useState(readEnrolledIdsBeforeAdd);
  const [addedToastDismissed, setAddedToastDismissed] = useState(false);
  // Back from the forced MFA login, the dialog that asked for it opens again for the user to confirm.
  const [turnOffOpen, setTurnOffOpen] = useState(() => searchParams.get(MFA_STEP_UP_PARAM) === MFA_STEP_UP_TURN_OFF);
  const [stepUpRemovalId, setStepUpRemovalId] = useState(() =>
    searchParams.get(MFA_STEP_UP_PARAM) === MFA_STEP_UP_REMOVE ? searchParams.get(MFA_STEP_UP_METHOD_PARAM) : null,
  );
  const [turnedOffToastOpen, setTurnedOffToastOpen] = useState(false);
  const [startFailed, setStartFailed] = useState(false);
  const returnedFromStepUp = searchParams.has(MFA_STEP_UP_PARAM);
  const startMyAccountOperation = useMyAccountOperation();
  // Every change signs the customer in again through Auth0. Android used to clear this page's
  // storage on the way back, so it stays read-only until it sets zbAppSession. iOS leaves
  // localStorage in place, which holds the login redirect, so enrollment and step-up still return
  // here; the "method added" confirmation, kept in sessionStorage, doesn't show until zbAppSession.
  const inWebView = Boolean(WebView?.isWebView);
  const keepsSignInState = !inWebView || appKeepsSignInState();
  // The installed app's user agent is "WebView/<version>" on an iPhone, which does not set
  // WebView.platform. Match that, and the "WebView/ios/<version>" form that does.
  const isIosApp =
    inWebView && (WebView?.platform === 'ios' || /iPhone|iPad|iPod/.test(window.navigator?.userAgent ?? ''));
  const canChange = keepsSignInState || isIosApp;

  const removalTargetFor = (method) => {
    const methodType = method && METHOD_TYPES.find(({ types }) => types.includes(method.type));
    if (!methodType) {
      return null;
    }
    const { key, label, name } = methodType;
    return { ...method, label: fm({ id: label }), detail: methodDetail(key, method), name: fm({ id: name }) };
  };

  useEffect(() => {
    try {
      sessionStorage.removeItem(ENROLLED_IDS_KEY);
    } catch {
      // Without storage the confirmation just doesn't show.
    }
  }, []);

  // Read into state above; dropped from the URL so a reload doesn't open the dialog again.
  useEffect(() => {
    if (!returnedFromStepUp) {
      return;
    }
    setSearchParams(
      (params) => {
        params.delete(MFA_STEP_UP_PARAM);
        params.delete(MFA_STEP_UP_METHOD_PARAM);
        return params;
      },
      { replace: true },
    );
  }, [returnedFromStepUp, setSearchParams]);

  const stepUp = (params) =>
    navigate(generateMfaStepUpUrl(`${accountSettingsSecurity}?${new URLSearchParams(params)}`));

  const handleTurnedOff = () => {
    setTurnOffOpen(false);
    clearMfaNudgeDecision();
    refetch();
    setTurnedOffToastOpen(true);
  };

  const turnOffToast = turnedOffToastOpen && (
    <Snackbar
      open
      variant="action"
      title={fm({ id: LOCALIZED_TEXT.turnedOff })}
      dismissable
      autoHideDuration={6000}
      onClose={() => setTurnedOffToastOpen(false)}
    />
  );

  const openInBrowser = (
    <Text component="p" size="sm" color="secondary">
      {fm({ id: LOCALIZED_TEXT.openInBrowser })}
    </Text>
  );

  if (loading) {
    return (
      <Styled.Card2fa>
        <Skeleton height="28px" width="40%" />
        <Skeleton height="80px" />
      </Styled.Card2fa>
    );
  }

  // A failed read must not look like "no methods enrolled" — that would invite the user to
  // re-enroll factors they already have.
  if (error) {
    return (
      <Styled.Card2fa>
        <ErrorState />
      </Styled.Card2fa>
    );
  }

  // Only a confirmed, non-recovery factor of a type we render actually satisfies MFA. customers-api's
  // `exists` flag also counts pending enrollments and recovery codes, so it can't drive On/Off, and a
  // type without a row would show On while leaving the user nothing to manage.
  const usableMethods = methods.filter((m) => m.confirmed && !m.isRecoveryMethod && RENDERED_METHOD_TYPES.has(m.type));
  // The removal rules count every confirmed method, as customers-api does.
  const confirmedMethods = methods.filter((m) => m.confirmed);
  const enrolledKeys = new Set(
    METHOD_TYPES.filter(({ types }) => usableMethods.some((m) => types.includes(m.type))).map(({ key }) => key),
  );

  const startEnrollment = (mfaFactor) => {
    try {
      sessionStorage.setItem(ENROLLED_IDS_KEY, JSON.stringify(usableMethods.map(({ id }) => id)));
    } catch {
      // Without storage the confirmation just doesn't show.
    }
    clearMfaNudgeDecision();
    navigate(enrollmentUrl(mfaFactor));
  };

  const removalTarget =
    methodToRemove ??
    (stepUpRemovalId ? removalTargetFor(usableMethods.find(({ id }) => id === stepUpRemovalId)) : null);

  const recoveryCode = methods.find((m) => m.confirmed && m.isRecoveryMethod);
  const recoveryCodeAdded = formatMfaDate(recoveryCode?.createdAt);
  const startRecoveryCode = async () => {
    // Auth0 accepts only the oldest recovery code at sign-in, so every listed one is replaced, through
    // customers-api. The callback finds a code left unconfirmed by an abandoned Universal Login
    // enrollment, which this list omits, through the My Account API.
    const started = await startMyAccountOperation(MY_ACCOUNT_OPERATIONS.recoveryCode, {
      returnTo: accountSettingsSecurity,
      replaceMethodIds: methods.filter((m) => m.isRecoveryMethod).map(({ id }) => id),
    });
    setStartFailed(!started);
  };

  const addedMethod = enrolledIdsBeforeAdd && usableMethods.find(({ id }) => !enrolledIdsBeforeAdd.includes(id));
  const addedMethodType = addedMethod && METHOD_TYPES.find(({ types }) => types.includes(addedMethod.type));

  // With no methods there's nothing to list, so the card offers the single general setup flow
  // (the customer picks the method in Auth0), as aligned on Sep 15.
  if (usableMethods.length === 0) {
    return (
      <Styled.Card2fa>
        <EmptyState
          title={fm({ id: LOCALIZED_TEXT.emptyStateTitle })}
          description={fm({
            id: requireMfa ? LOCALIZED_TEXT.emptyStateSmsOnlyDescription : LOCALIZED_TEXT.emptyStateDescription,
          })}
          slots={{ image: Styled.EmptyStateIcon }}
          slotProps={
            canChange
              ? {
                  primaryAction: {
                    label: fm({ id: LOCALIZED_TEXT.emptyStateSetUp }),
                    variant: 'contained',
                    color: 'primary',
                    clickstreamProps: { appSection: 'account', elementName: 'two_step_verification_set_up' },
                    onClick: () => startEnrollment(),
                  },
                }
              : {}
          }
        />
        {!canChange && openInBrowser}
        {turnOffToast}
      </Styled.Card2fa>
    );
  }

  return (
    <Styled.Card2fa>
      <Styled.HeaderRow>
        <Heading component="h2" size="lg">
          {fm({ id: LOCALIZED_TEXT.title })}
        </Heading>
        <Chip color="success" label={fm({ id: LOCALIZED_TEXT.on })} size="small" />
      </Styled.HeaderRow>
      <Styled.MethodList>
        {METHOD_TYPES.map(({ key, types, label: labelId, description, recommendation, mfaFactor, Icon }) => {
          const label = fm({ id: labelId });
          const method = usableMethods.find((m) => types.includes(m.type));
          const enrolled = Boolean(method);
          if (NOT_FOR_REQUIRE_MFA.has(key) && !enrolled && requireMfa) {
            return null;
          }
          const detail = methodDetail(key, method);
          const added = formatMfaDate(method?.createdAt);
          const lastUsed = formatMfaDate(method?.lastAuth);
          const removalBlockedReason = enrolled ? getRemovalBlockedReason(method, confirmedMethods, requireMfa) : null;
          const showRecommendation = !enrolled && key === 'otp' && enrolledKeys.has('sms');

          return (
            <Styled.MethodRow key={key}>
              <Styled.MethodIcon
                sx={{
                  backgroundColor: enrolled ? theme.palette.background.blue.light : theme.palette.background.grey.dark,
                }}
              >
                <Icon fontSize="small" />
              </Styled.MethodIcon>
              <Styled.MethodText>
                <Text component="p" size="md" weight="medium" color="primary">
                  {label}
                </Text>
                {enrolled ? (
                  <>
                    {detail && (
                      <Text component="p" size="sm" color="secondary">
                        {detail}
                      </Text>
                    )}
                    {(added || lastUsed) && (
                      <Text component="p" size="sm" color="secondary">
                        {added && fm({ id: LOCALIZED_TEXT.added }, { date: added })}
                        {added && lastUsed && ' · '}
                        {lastUsed && fm({ id: LOCALIZED_TEXT.lastUsed }, { date: lastUsed })}
                      </Text>
                    )}
                    {removalBlockedReason && (
                      <Text component="p" size="sm" color="secondary">
                        {fm({ id: removalBlockedReason.id }, removalBlockedReason.values)}
                      </Text>
                    )}
                  </>
                ) : (
                  <>
                    <Text component="p" size="sm" color="secondary">
                      {fm({ id: description })}
                    </Text>
                    {showRecommendation && (
                      <Text component="p" size="sm" color="secondary">
                        {fm({ id: recommendation })}
                      </Text>
                    )}
                  </>
                )}
              </Styled.MethodText>
              {canChange &&
                (enrolled ? (
                  <IconButton
                    label={fm({ id: LOCALIZED_TEXT.remove }, { method: label })}
                    data-testid={`remove-${key}`}
                    disabled={Boolean(removalBlockedReason)}
                    onClick={() => setMethodToRemove(removalTargetFor(method))}
                    icon={<DeleteRoundedIcon />}
                  />
                ) : (
                  <Button
                    data-testid={`add-${key}`}
                    aria-label={fm({ id: LOCALIZED_TEXT.addMethod }, { method: label })}
                    clickstreamProps={{ appSection: 'account', elementName: 'two_step_verification_add' }}
                    onClick={() => startEnrollment(mfaFactor)}
                    size="sm"
                    variant="text"
                    color="primary"
                  >
                    {fm({ id: LOCALIZED_TEXT.add })}
                  </Button>
                ))}
            </Styled.MethodRow>
          );
        })}
        <Styled.MethodRow>
          <Styled.MethodIcon
            sx={{
              backgroundColor: recoveryCode ? theme.palette.background.blue.light : theme.palette.background.grey.dark,
            }}
          >
            <PasswordRoundedIcon fontSize="small" />
          </Styled.MethodIcon>
          <Styled.MethodText>
            <Text component="p" size="md" weight="medium" color="primary">
              {fm({ id: LOCALIZED_TEXT.recoveryCodeLabel })}
            </Text>
            <Text component="p" size="sm" color="secondary">
              {recoveryCodeAdded
                ? fm({ id: LOCALIZED_TEXT.added }, { date: recoveryCodeAdded })
                : fm({ id: LOCALIZED_TEXT.recoveryCodeDescription })}
            </Text>
          </Styled.MethodText>
          {keepsSignInState && (
            <Button
              data-testid="generate-recovery-code"
              clickstreamProps={{
                appSection: 'account',
                elementName: recoveryCode
                  ? 'two_step_verification_regenerate_recovery_code'
                  : 'two_step_verification_generate_recovery_code',
              }}
              onClick={startRecoveryCode}
              size="sm"
              variant="text"
              color="primary"
            >
              {fm({ id: recoveryCode ? LOCALIZED_TEXT.recoveryCodeRegenerate : LOCALIZED_TEXT.recoveryCodeGenerate })}
            </Button>
          )}
        </Styled.MethodRow>
      </Styled.MethodList>
      {!canChange && openInBrowser}
      {!requireMfa && canChange && (
        <Styled.TurnOffRow>
          <Button
            clickstreamProps={{ appSection: 'account', elementName: 'two_step_verification_turn_off' }}
            onClick={() => setTurnOffOpen(true)}
            size="sm"
            variant="text"
            color="primary"
          >
            {fm({ id: LOCALIZED_TEXT.turnOff })}
          </Button>
        </Styled.TurnOffRow>
      )}
      {turnOffOpen && (
        <TurnOffMfaDialog
          isOpen
          onClose={() => setTurnOffOpen(false)}
          onTurnedOff={handleTurnedOff}
          onStepUpRequired={() => stepUp({ [MFA_STEP_UP_PARAM]: MFA_STEP_UP_TURN_OFF })}
        />
      )}
      {removalTarget && (
        <RemoveMfaMethodDialog
          isOpen
          method={removalTarget}
          requireMfa={requireMfa}
          onStepUpRequired={() =>
            stepUp({ [MFA_STEP_UP_PARAM]: MFA_STEP_UP_REMOVE, [MFA_STEP_UP_METHOD_PARAM]: removalTarget.id })
          }
          // Refetch on every close, not only after a removal: a refusal or notFound means the list
          // was stale, and the fresh one lets the card block the removal upfront.
          onClose={() => {
            setMethodToRemove(null);
            setStepUpRemovalId(null);
            clearMfaNudgeDecision();
            refetch();
          }}
        />
      )}
      {addedMethodType && !addedToastDismissed && (
        <Snackbar
          open
          variant="action"
          title={fm({ id: LOCALIZED_TEXT.methodAdded }, { method: fm({ id: addedMethodType.label }) })}
          dismissable
          autoHideDuration={6000}
          onClose={() => setAddedToastDismissed(true)}
        />
      )}
      {startFailed && (
        <Snackbar
          open
          variant="alert"
          title={fm({ id: LOCALIZED_TEXT.startFailed })}
          dismissable
          autoHideDuration={6000}
          onClose={() => setStartFailed(false)}
        />
      )}
      {turnOffToast}
    </Styled.Card2fa>
  );
}

export default TwoStepVerification;
